import React, { useMemo } from "react";
import { useState, useEffect } from "react";
import {
  format,
  startOfMonth,
  addMonths,
  startOfDay,
  parseISO,
  addDays,
} from "date-fns";
import classnames from "classnames";

import { Event } from "../../api";
import { useTypedSelector } from "../../store/models/root.interface";
import Loader from "../../common/components/Loader";
import CalendarDate from "./CalendarDate";

type DayEvents = { [key: string]: { date: Date; events: Event[] } };

function getTime(start: Date, end: Date, date: Date) {
  const startOfDay = date.valueOf();
  const endOfDay = addDays(date, 1).valueOf();
  const startOfEvent = start.valueOf();
  const endOfEvent = end.valueOf();
  if (startOfDay < startOfEvent && startOfEvent < endOfDay) {
    if (startOfDay < endOfEvent && endOfEvent < endOfDay) {
      return format(start, "p") + " - " + format(end, "p");
    }
    return format(start, "p");
  }
}

const EventsDiary: React.FC = () => {
  const eventsByUId = useTypedSelector((state) => state.event.eventsByUid);
  const diary = useTypedSelector((state) => state.event.diary);

  const [key, setKey] = useState(format(diary.date, "yyyy-MM"));
  const [monthName, setMonthName] = useState(format(diary.date, "MMMM"));
  const [events, setEvents] = useState<DayEvents>({});

  const isLoading = useMemo(() => diary.eventUids[key] === "loading", [
    key,
    diary.eventUids,
  ]);
  const isError = useMemo(() => diary.eventUids[key] === "error", [
    key,
    diary.eventUids,
  ]);
  const anyItems = useMemo(() => {
    const value = diary.eventUids[key];
    return (
      !value ||
      value === "loading" ||
      (typeof value !== "string" && value.length)
    );
  }, [key, diary.eventUids]);

  const getEvents = () => {
    const min = startOfMonth(diary.date);
    const max = addMonths(min, 1);
    const data = diary.eventUids[key];
    let obj: DayEvents = {};
    if (data && typeof data !== "string") {
      for (let i = 0; i < data.length; i++) {
        let event = eventsByUId[data[i]];
        if (event && !event.isRound) {
          let start = parseISO(event.start);
          let end = parseISO(event.end);
          for (let d = startOfDay(start); d < end; d = addDays(d, 1)) {
            if (d < min) {
              continue;
            }
            if (max <= d) {
              break;
            }
            let key = "D" + format(d, "dd");
            if (!(key in obj)) {
              obj[key] = { date: d, events: [] };
            }
            obj[key].events.push(event);
          }
        }
      }
    }
    return obj;
  };

  // Runs when the date is changed
  useEffect(() => {
    setKey(format(diary.date, "yyyy-MM"));
  }, [diary.date]);

  // Runs when the date is changed
  useEffect(() => {
    const data = diary.eventUids[key];
    if (data && typeof data !== "string") {
      // Events have been received
      setMonthName(format(diary.date, "MMMM"));
      setEvents(getEvents());
    }
  }, [key]);

  // Runs when the events are requested or received
  useEffect(() => {
    const data = diary.eventUids[key];
    if (data && data !== "loading") {
      // Events have been received
      setMonthName(format(diary.date, "MMMM"));
      setEvents(getEvents());
    }
  }, [diary.eventUids]);

  // Runs on init
  useEffect(() => {
    const data = diary.eventUids[key];
    if (data && data !== "loading") {
      setMonthName(format(diary.date, "MMMM"));
      setEvents(getEvents());
    }
  }, []);

  return (
    <div className="flex-grow-1 mh-0 d-flex flex-column position-relative">
      <div className="text-secondary text-uppercase border-bottom bg-light fs-9 px-3 py-2">
        <strong>{monthName} Events</strong>
      </div>
      {isError ? (
        <p>Sorry, something went wrong.</p>
      ) : anyItems ? (
        <ul className="list-none flex-grow-1 mh-0 scroll-auto custom-scrollbar">
          {Object.keys(events).map((key, i) => (
            <li key={i} className="border-bottom d-flex py-2 bg-light">
              <div className="px-3">
                <CalendarDate date={events[key].date} />
              </div>
              <div className="pr-3 mw-0">
                {events[key].events.map((event, index) => (
                  <div
                    key={event.id}
                    className={classnames({
                      "mt-2": index !== 0,
                    })}
                  >
                    <div className="text-secondary text-truncate fw-6">
                      <span title={event.name}>{event.name}</span>
                    </div>
                    <div className="d-flex fs-10 fw-6">
                      {/* {event.round && (
                        <React.Fragment>
                          <span className="flex-shrink-1 text-truncate mw-0">
                            <span
                              className="text-uppercase"
                              title={event.round}
                            >
                              {event.round}
                            </span>
                          </span>
                          <span className="mx-2">|</span>
                        </React.Fragment>
                      )} */}
                      <span className="text-nowrap text-lowercase">
                        {getTime(
                          parseISO(event.start),
                          parseISO(event.end),
                          events[key].date)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="p-3">No events available</p>
      )}
      <Loader show={isLoading} />
    </div>
  );
};

export default EventsDiary;
